import {optionalMaterialInput} from "#material/nodes/base/connection/validation/parameters"
import type {MaterialNodeDefinition} from "#material/nodes/base/material/types/definition"
import {ExchangeNodeName} from "#material/exchange/node-name"
import {materialColorOutput} from "#material/nodes/base/material/validation/io"
import {materialUiSchema} from "#material/nodes/base/material/validation/ui"
import type {RgbBlendType} from "#material/nodes/color/mix-rgb/validation"
import {renameBlendTypesToBlender, rgbBlendType} from "#material/nodes/color/mix-rgb/validation"
import type {MaterialNodeSockets} from "#material/types/material-node-sockets"
import {color, versionChain} from "@cm/graph-v3"
import {z} from "zod/v4-mini"

export interface MixRgbSockets extends MaterialNodeSockets {
    inputs: {
        color1: "color" // found in inputs: float,color,point,normal; found in settings: color; inferred from data: float3
        color2: "color" // found in inputs: color,float,point,normal; found in settings: color; inferred from data: float3
        fac: "float" // found in inputs: color,float; found in settings: float,scalar; inferred from data: float
    }
    settings: {
        color: "color" // found in inputs: -; found in settings: color; inferred from data: float3
        color1: "color" // found in inputs: float,color,point,normal; found in settings: color; inferred from data: float3
        color2: "color" // found in inputs: color,float,point,normal; found in settings: color; inferred from data: float3
        fac: "float" // found in inputs: color,float; found in settings: float,scalar; inferred from data: float
        useAlpha: "boolean" // found in inputs: -; found in settings: bool; inferred from data: boolean
        useClamp: "boolean" // found in inputs: -; found in settings: bool; inferred from data: boolean
    }
    settingsExtensions: {
        blendType?: RgbBlendType // found in inputs: -; found in settings: string; inferred from data: string
    }
    outputs: {
        color: "color" // found in inputs: -; found in settings: string; inferred from data: string
    }
}

export const mixRgbDefinition = {
    exchangeFormat: {
        nodeName: ExchangeNodeName.ShaderNodeMixRGB,
    },
    nodeClass: "MixRgb",
    versionChain: versionChain([renameBlendTypesToBlender]),
    ui: {
        label: "RGB Mix",
        color: "#8d802f",
        items: [
            {name: "color", valueType: "color", label: "Color", output: true},
            {name: "fac", valueType: "float", label: "Fac", range: {min: 0, max: 1}, input: true, setting: true, defaultValue: 0.5},
            {name: "color1", valueType: "color", label: "Color1", input: true, setting: true, defaultValue: {r: 0.0, g: 0.0, b: 0.0}},
            {name: "color2", valueType: "color", label: "Color2", input: true, setting: true, defaultValue: {r: 0.0, g: 0.0, b: 0.0}},
            {
                defaultValue: "MIX",
                name: "blendType",
                label: "Blend Type",
                valueType: "string",
                options: [
                    {label: "Mix", value: "MIX"},
                    {label: "Darken", value: "DARKEN"},
                    {label: "Multiply", value: "MULTIPLY"},
                    {label: "Color Burn", value: "BURN"},
                    {label: "Lighten", value: "LIGHTEN"},
                    {label: "Screen", value: "SCREEN"},
                    {label: "Color Dodge", value: "DODGE"},
                    {label: "Add", value: "ADD"},
                    {label: "Overlay", value: "OVERLAY"},
                    {label: "Soft Light", value: "SOFT_LIGHT"},
                    {label: "Linear Light", value: "LINEAR_LIGHT"},
                    {label: "Difference", value: "DIFFERENCE"},
                    {label: "Exclusion", value: "EXCLUSION"},
                    {label: "Subtract", value: "SUBTRACT"},
                    {label: "Divide", value: "DIVIDE"},
                    {label: "Hue", value: "HUE"},
                    {label: "Saturation", value: "SATURATION"},
                    {label: "Color", value: "COLOR"},
                    {label: "Value", value: "VALUE"},
                ],
                input: false,
                setting: true,
            },
            {name: "useClamp", label: "Clamp", valueType: "boolean", defaultValue: false, input: false, setting: true},
        ],
        support: {
            cycles: true,
            three: true,
        },
    },
    validation: {
        returns: z.strictObject({color: materialColorOutput}),
        parameters: z.strictObject({
            inputs: z.strictObject({
                color1: optionalMaterialInput,
                color2: optionalMaterialInput,
                fac: optionalMaterialInput,
            }),
            settings: z.strictObject({
                blendType: z.optional(rgbBlendType),
                color: z.optional(color),
                color1: z.optional(color),
                color2: z.optional(color),
                fac: z.optional(z.number()),
                useClamp: z.optional(z.boolean()),
                useAlpha: z.optional(z.boolean()),
            }),
            ui: materialUiSchema,
        }),
    },
} satisfies MaterialNodeDefinition<MixRgbSockets>
